import type { ExecutionStatus } from '../types/board.js';

const BOARD_URL = '/api/board';

export interface TimelinePayload {
  executionId: string;
  status: ExecutionStatus;
  model: string | null;
  startedAt: string | null;
  firstOutputAt: string | null;
  lastOutputAt: string | null;
  completedAt: string | null;
  warningEmittedAt: string | null;
  queuePosition: number | null;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export interface StartExecutionRequest {
  model?: string;
  sourceType?: 'specwright' | 'backlog' | 'todo';
  projectId?: number | string;
  projectPath?: string;
}

export interface ExecutionJob {
  id: string;
  ticketId: string;
  sourceType: string;
  projectId: string;
  model: string;
  status: string;
  enqueuedAt: string;
  startedAt?: string;
}

export interface ExecutionQueueStatus {
  runningJobs: ExecutionJob[];
  queuedJobs: ExecutionJob[];
}

export interface ExecutionHistoryEntry {
  id: number;
  ticket_id: string;
  source_type: string;
  project_id: string;
  model: string;
  status: string;
  started_at: string;
  completed_at?: string;
  exit_code?: number;
  error_message?: string;
}

export type StartExecutionResponse =
  | { job: ExecutionJob; status: 'enqueued' }
  | { status: 'pending-clarification' | 'plan-phase-started'; ticketId: string };

export async function startExecution(ticketId: string, options: StartExecutionRequest): Promise<StartExecutionResponse> {
  return fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });
}

export async function stopExecution(ticketId: string): Promise<void> {
  await fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/execute`, {
    method: 'DELETE',
  });
}

export async function getExecutionStatus(): Promise<ExecutionQueueStatus> {
  return fetchJson(`${BOARD_URL}/execution/status`);
}

export async function getExecutionHistory(limit = 50): Promise<ExecutionHistoryEntry[]> {
  return fetchJson(`${BOARD_URL}/execution/history?limit=${limit}`);
}

export async function getItemModel(ticketId: string): Promise<string> {
  const result = await fetchJson<{ model: string }>(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/model`);
  return result.model;
}

export async function setModel(ticketId: string, model: string): Promise<void> {
  await fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/model`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model }),
  });
}

export async function getExecutionTimeline(ticketId: string): Promise<TimelinePayload | null> {
  try {
    return await fetchJson<TimelinePayload>(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/execution-status`);
  } catch {
    return null;
  }
}

export async function preflightAnswer(ticketId: string, answers: Array<{ question: string; answer: string }>): Promise<void> {
  await fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/preflight-answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export async function approvePlan(ticketId: string): Promise<void> {
  await fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/plan-approve`, {
    method: 'POST',
  });
}

export async function rejectPlan(ticketId: string): Promise<void> {
  await fetchJson(`${BOARD_URL}/items/${encodeURIComponent(ticketId)}/plan-reject`, {
    method: 'POST',
  });
}
